<script lang="ts">
// @ts-ignore - compat: Vue 2 + composition API
import { defineComponent, ref, computed, watch, nextTick, onMounted } from 'vue';
import { SendIcon, XIcon } from 'vue-feather-icons';

type TTag =
  | 'Desatualizada'
  | 'Fora de contexto'
  | 'Não corresponde à pergunta'
  | 'Resposta incompleta'
  | 'Outro';

export default defineComponent({
  name: 'FeedbackDialog',
  components: { SendIcon, XIcon },
  props: {
    show: { type: Boolean, required: true },
    title: { type: String, default: 'O que há de errado na resposta?' },
  },
  emits: ['close', 'submit'],
  setup(props: { show: boolean }, { emit }: { emit: (event: 'close' | 'submit', payload?: any) => void }) {
    const description = ref('');
    const textareaRef = ref<HTMLTextAreaElement | null>(null);
    const availableTags = ref<TTag[]>([
      'Desatualizada',
      'Fora de contexto',
      'Não corresponde à pergunta',
      'Resposta incompleta',
      'Outro',
    ]);
    const selectedTags = ref<TTag[]>([]);

    const hasSelection = computed(() => description.value.trim().length > 0 || selectedTags.value.length > 0);

    onMounted(() => {
      nextTick(() => textareaRef.value = this.$refs.textareaRef);
    });

    const toggleTag = (tag: TTag) => {
      const idx = selectedTags.value.indexOf(tag);
      if (idx >= 0) selectedTags.value.splice(idx, 1);
      else selectedTags.value.push(tag);
      nextTick(() => textareaRef.value?.focus());
    };

    const handleClose = () => emit('close');
    const handleSubmit = () => {
      emit('submit', {
        description: description.value.trim(),
        tags: [...selectedTags.value],
      });
      emit('close');
      description.value = '';
      selectedTags.value = [];
    };

    // foca sempre que abrir
    watch(
      () => props.show,
      (val: boolean) => {
        if (val) nextTick(() => textareaRef.value?.focus());
      },
      { immediate: true }
    );

    return { description, availableTags, selectedTags, toggleTag, handleClose, handleSubmit, hasSelection, textareaRef };
  },
});
</script>

<template>
  <transition name="fade">
    <div v-if="show" class="w-full max-w-[100%]">
      <div class="relative z-[60] rounded-2xl border border-[#e3dbf5] bg-white p-4 shadow-md">
        <button
          @click="handleClose"
          class="absolute right-2 top-2 text-gray-400 hover:text-purple-500 transition"
          aria-label="Fechar feedback"
        >
          <XIcon size="16" />
        </button>
        <h3 class="text-sm font-semibold text-gray-800 mb-3">
          <strong>O que há de errado na resposta?</strong>
        </h3>

        <textarea
          ref="textareaRef"
          v-model="description"
          rows="3"
          class="w-full text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-300 p-3 placeholder-gray-400"
          placeholder="Descreva o problema aqui..."
        />

        <div class="mt-3 flex flex-wrap gap-2">
          <button
            v-for="tag in availableTags"
            :key="tag"
            type="button"
            @click="toggleTag(tag)"
            :class="[
              'text-xs px-3 py-1 rounded-full border transition select-none',
              selectedTags.includes(tag)
                ? 'bg-purple-100 border-purple-300 text-purple-700'
                : 'bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200'
            ]"
          >
            {{ tag }}
          </button>
        </div>

        <div class="mt-4">
          <button
            @click="handleSubmit"
            :disabled="!hasSelection"
            class="inline-flex items-center w-full justify-center gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2 transition"
          >
            <SendIcon size="16" />
            Enviar feedback
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active { transition: opacity .15s; }
.fade-enter-from,
.fade-leave-to { opacity: 0; }
</style>


